// Deliverables and their subtasks: assignee, due date, status, stock flag, a
// timer, and the review order inside each one.

import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import Appear from '../components/ui/Appear';
import Avatar from '../components/ui/Avatar';
import { Button, IconButton, buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { EmptyState } from '../components/ui/EmptyState';
import Field from '../components/ui/Field';
import Panel from '../components/ui/Panel';
import Progress from '../components/ui/Progress';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import Select from '../components/ui/Select';
import { Tag, readable } from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import Tooltip from '../components/ui/Tooltip';
import {
  IconCalendar, IconCheck, IconChevron, IconClock, IconClose, IconPlay, IconPlus, IconStop,
} from '../components/ui/icons';
import {
  CONTROL_MOTION, EASE, FOCUS, MS, PRESS, STATE, TOUCH, reducedMotion, useFlip, useTicker,
} from '../components/ui/motion';
import { holdScopes, liveScope } from '../live/useLiveScopes';
import {
  addTask, deleteTask, startTimer, stopTimer, updateTask,
} from './sparkApi';
import type { Person, ProductionDetail, Task, TaskPatch, TimeEntry } from './sparkApi';
import SparkReviewPanel from './SparkReviewPanel';
import { daysUntil, useBecame } from './SparkProps';
import { STUDIO_ROLES, TASK_STATUS, eligibilityText, eligibleFor } from './sparkRules';
import { dayText, errorCode, errorMessage, ymdValue } from './sparkUi';


/** The take-off sequence when the last deliverable lands: one check's lift, and the gap between two. */
const LIFT_MS = 520;
const LIFT_STEP_MS = 90;

/** A status the chain or the event owns (not set by hand): the same tinted plate as the status
 *  well beside it, without the chevron, so every row's status lines up. */
const StatusPlate: React.FC<{ status: Task['status'] }> = ({ status }) => {
  const t = TASK_STATUS[status];
  if (!t) return null;
  return (
    <span
      className="flex h-7 w-full min-w-0 items-center rounded-control px-2.5 text-[13px] font-semibold"
      style={{ backgroundColor: `color-mix(in srgb, ${t.color} 20%, var(--surface-container-high))`, color: readable(t.color) }}
    >
      <span className="truncate">{t.label}</span>
    </span>
  );
};

const HAND_STATUSES: { key: 'not_started' | 'in_progress' | 'ready'; label: string }[] = [
  { key: 'not_started', label: 'Not started' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'ready', label: 'Ready to Fly' },
];
const isHand = (s: string) => HAND_STATUSES.some((h) => h.key === s);
const STATUS_OPTIONS = HAND_STATUSES.map((h) => ({ key: h.key, label: h.label, hex: TASK_STATUS[h.key].color }));

const fmtClock = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const fmtMinutes = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`);

// ── Ready to Fly ─────────────────────────────────────────────────────────────

/** Nine glints thrown outward from the middle of their host, on a ring with a little jitter. */
const SPARKS = Array.from({ length: 9 }, (_, i) => {
  const a = (i / 9) * Math.PI * 2 - Math.PI / 2 + (i % 2 ? 0.25 : -0.1);
  const d = 26 + (i % 3) * 9;
  return { dx: Math.cos(a) * d, dy: Math.sin(a) * d * 0.7, size: i % 3 === 0 ? 'h-2.5 w-2.5' : 'h-1.5 w-1.5' };
});

/** A small burst of gold sparks, once, for a deliverable reaching Ready to Fly. Movement only, so it is skipped under reduced motion. */
const SparkBurst: React.FC = () => {
  const host = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = host.current;
    if (!el || reducedMotion()) return undefined;
    const nodes = Array.from(el.children) as HTMLElement[];
    if (!nodes.length || typeof nodes[0].animate !== 'function') return undefined;
    const runs = nodes.map((n, i) => {
      const { dx, dy } = SPARKS[i];
      return n.animate(
        [
          { transform: 'translate(-50%, -50%) scale(0.3) rotate(0deg)', opacity: 0 },
          { transform: `translate(calc(${dx * 0.55}px - 50%), calc(${dy * 0.55}px - 50%)) scale(1) rotate(45deg)`, opacity: 1, offset: 0.3 },
          { transform: `translate(calc(${dx}px - 50%), calc(${dy + 6}px - 50%)) scale(0.2) rotate(90deg)`, opacity: 0 },
        ],
        { duration: 600, delay: i * 14, easing: EASE.emphasized, fill: 'both' },
      );
    });
    return () => runs.forEach((r) => r.cancel());
  }, []);
  return (
    <span ref={host} aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 z-10">
      {SPARKS.map((s, i) => (
        <span key={i} className={`absolute left-0 top-0 text-primary opacity-0 drop-shadow-glow ${s.size}`}>
          <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
            <path d="M12 1c.9 6.2 4.8 10.1 11 11-6.2.9-10.1 4.8-11 11-.9-6.2-4.8-10.1-11-11 6.2-.9 10.1-4.8 11-11z" fill="currentColor" />
          </svg>
        </span>
      ))}
    </span>
  );
};

// ── Timer ────────────────────────────────────────────────────────────────────

/**
 * One key for both states, so starting it is a morph and not a swap: the play mark turns to
 * stop, the elapsed time slides out beside it, and the key lights up and breathes while live.
 */
const TimerKey: React.FC<{
  task: Task;
  running: TimeEntry | null;
  onStart: () => void;
  onStop: () => void;
}> = ({ task, running, onStart, onStop }) => {
  const isRunning = running?.task_id === task.id;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isRunning) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isRunning]);

  const elapsed = isRunning && running ? Math.max(0, now - new Date(running.started_at).getTime()) : 0;
  const logged = task.minutes !== undefined && task.minutes > 0 ? fmtMinutes(task.minutes) : null;
  const slide = 'overflow-hidden whitespace-nowrap text-xs tabular-nums transition-[max-width,opacity,margin] duration-standard ease-emphasized motion-reduce:transition-[opacity]';

  return (
    <span className="inline-flex items-center justify-end">
      <span className={`${slide} text-on-surface-variant ${logged && !isRunning ? 'mr-1 max-w-[4rem] opacity-100' : 'mr-0 max-w-0 opacity-0'}`}>
        {logged}
      </span>
      <Tooltip label={isRunning ? 'Stop timer' : 'Start timer'} note={isRunning ? undefined : logged ? `${logged} logged so far` : undefined}>
        <button
          type="button"
          onClick={isRunning ? onStop : onStart}
          aria-label={isRunning ? `Stop the timer on ${task.name}` : `Start timer on ${task.name}`}
          className={`inline-flex h-7 min-w-7 items-center justify-center rounded-control px-1.5 ${STATE} ${CONTROL_MOTION} ${PRESS} ${FOCUS} ${TOUCH} ${
            isRunning ? 'bg-primary/15 text-primary-ink shadow-glow' : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          {isRunning && (
            <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-control shadow-glow-soft motion-safe:animate-pulse" />
          )}
          <span className="flex h-4 w-4 flex-none items-center justify-center">
            {isRunning ? <IconStop className="h-2.5 w-2.5" /> : <IconPlay className="h-3 w-3" />}
          </span>
          <span className={`${slide} ${isRunning ? 'ml-1.5 max-w-[4.5rem] opacity-100' : 'ml-0 max-w-0 opacity-0'}`}>
            {isRunning ? fmtClock(elapsed) : ''}
          </span>
        </button>
      </Tooltip>
    </span>
  );
};

// ── Due date ─────────────────────────────────────────────────────────────────

/** A dated key, or a quiet calendar key when undated. Pressing either opens the date field in place. */
const DueDate: React.FC<{ task: Task; onChange: (value: string | null) => void }> = ({ task, onChange }) => {
  const [editing, setEditing] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const giveBack = useRef(false);

  useEffect(() => {
    if (editing) {
      input.current?.focus();
      try { input.current?.showPicker?.(); } catch { /* needs a user gesture in some browsers; the field is focused either way */ }
    } else if (giveBack.current) {
      giveBack.current = false;
      button.current?.focus();
    }
  }, [editing]);

  if (editing) {
    return (
      <Field
        ref={input}
        size="sm"
        type="date"
        aria-label={`Due date for ${task.name}`}
        value={ymdValue(task.due_date)}
        onChange={(e) => onChange(e.target.value || null)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') { giveBack.current = true; setEditing(false); }
        }}
        className="px-2 tabular-nums [color-scheme:dark] motion-safe:animate-menuIn"
      />
    );
  }

  if (!task.due_date) {
    return (
      <Tooltip label="Set a due date">
        <IconButton ref={button} size="sm" label={`Add date for ${task.name}`} onClick={() => setEditing(true)}>
          <IconCalendar className="h-3.5 w-3.5" />
        </IconButton>
      </Tooltip>
    );
  }

  const text = dayText(task.due_date, 'MMM D');
  const late = task.status !== 'ready' && task.status !== 'closed' && daysUntil(task.due_date) < 0;
  return (
    <Tooltip label="Change the due date" note={late ? 'Past due' : undefined}>
      <button
        ref={button}
        type="button"
        aria-label={`${text}${late ? ', past due' : ''}, due date for ${task.name}`}
        onClick={() => setEditing(true)}
        className={`${buttonClass('tonal', 'sm')} w-full tabular-nums ${late ? '!bg-red-500/15 !text-red-300' : ''}`}
      >
        {late ? <IconClock className="h-3.5 w-3.5 flex-none" /> : <IconCalendar className="h-3.5 w-3.5 flex-none text-on-surface-variant" />}
        {text}
      </button>
    </Tooltip>
  );
};

// ── Assignee ─────────────────────────────────────────────────────────────────

const AssigneePicker: React.FC<{
  task: Task;
  eligibilityName: string;
  people: Person[];
  onPick: (person: Person | null) => void;
  onClose: () => void;
}> = ({ task, eligibilityName, people, onPick, onClose }) => {
  const [all, setAll] = useState(false);
  const studioPeople = people.filter((p) => p.studio);
  const yes = studioPeople.filter((p) => eligibleFor(eligibilityName, p.roles));
  const no = studioPeople.filter((p) => !eligibleFor(eligibilityName, p.roles));
  const roleText = (p: Person) => p.roles.map((r) => STUDIO_ROLES.find((x) => x.key === r)?.label || r).join(', ') || 'Studio';

  const person = (p: Person) => {
    const on = task.assignee_id === p.id;
    return (
      <li key={p.id}>
        <button
          type="button"
          aria-pressed={on}
          onClick={() => onPick(p)}
          className={`flex w-full items-center gap-2.5 rounded-control px-2 py-1.5 text-left ${STATE} ${CONTROL_MOTION} ${FOCUS} ${on ? 'bg-primary/15' : ''}`}
        >
          <Avatar name={p.name} userId={p.id} color={p.avatar_color} shape={p.avatar_shape} accessory={p.avatar_accessory} decorative ring="ring-transparent" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-on-surface">{p.name}</span>
            <span className="block truncate text-xs text-on-surface-variant">{roleText(p)}</span>
          </span>
          <IconCheck className={`h-4 w-4 flex-none text-primary transition-[opacity,transform] duration-quick ease-emphasized ${on ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`} />
        </button>
      </li>
    );
  };

  return (
    <div role="group" aria-label={`Who takes ${task.name}`} className="mt-1 rounded-control bg-surface-container-high p-1.5">
      <p className="m-0 px-2 pb-1 pt-0.5 text-xs text-on-surface-variant">{eligibilityText(eligibilityName)}</p>
      <ul className="m-0 list-none p-0">{yes.map(person)}</ul>
      {yes.length === 0 && <p className="m-0 px-2 py-1 text-sm text-on-surface-variant">Nobody in the Studio usually takes this.</p>}
      <Reveal show={all && no.length > 0}>
        <p className="m-0 px-2 pb-1 pt-2 text-xs text-on-surface-variant">Everyone else in the Studio. Fine if you need them.</p>
        <ul className="m-0 list-none p-0">{no.map(person)}</ul>
      </Reveal>
      <div className="mt-1 flex flex-wrap gap-1">
        <Appear show={!all && no.length > 0} className="inline-flex"><Button variant="quiet" size="sm" onClick={() => setAll(true)}>Show everyone</Button></Appear>
        <Appear show={!!task.assignee_id} className="inline-flex"><Button variant="quiet" size="sm" onClick={() => onPick(null)}>Unassign</Button></Appear>
        <Button variant="quiet" size="sm" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
};

// ── Rows ─────────────────────────────────────────────────────────────────────

interface ListProps {
  detail: ProductionDetail;
  approver: boolean;
  archived: boolean;
  people: Person[];
  studio: boolean;
  userId: string;
  running: TimeEntry | null;
  onRunning: (entry: TimeEntry | null) => void;
  onTaskUpdated: (task: Task) => void;
  onChanged: () => void;
}

const AddLine: React.FC<{ label: string; placeholder: string; onAdd: (name: string) => Promise<void>; className?: string }> = ({ label, placeholder, onAdd, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await onAdd(name.trim());
      setName('');
    } catch {
      /* caller toasts */
    }
    setBusy(false);
  };
  return (
    <div className={className}>
      <Reveal show={!open}>
        <Button variant="quiet" size="sm" leading={<IconPlus className="h-3.5 w-3.5" />} onClick={() => setOpen(true)}>{label}</Button>
      </Reveal>
      <Reveal show={open}>
        <form onSubmit={submit} className="flex flex-wrap items-center gap-2 p-0.5">
          <Field
            autoFocus
            aria-label={label}
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
            placeholder={placeholder}
            className="min-w-[12rem] flex-1"
          />
          <Button type="submit" variant="filled" disabled={busy || !name.trim()}>Add</Button>
          <Button variant="quiet" onClick={() => setOpen(false)}>Done</Button>
        </form>
      </Reveal>
    </div>
  );
};

const DeliverableRow: React.FC<ListProps & { task: Task; subs: Task[]; launch?: { id: number; delay: number } }> = ({
  detail, people, studio, approver, archived, userId, running, onRunning, onTaskUpdated, onChanged, task, subs, launch,
}) => {
  const toast = useToast();
  const confirm = useConfirm();
  const waitingOnMe = detail.reviews.some((r) => r.task_id === task.id && r.status === 'waiting' && r.reviewer_id === userId);
  const [open, setOpen] = useState(waitingOnMe);
  const [picking, setPicking] = useState<string | null>(null);
  const closed = task.status === 'closed';
  const canEdit = studio && !closed;
  const ready = task.status === 'ready';
  const landed = useBecame(ready);
  // Not useFlip: the deliverable list's own flip would pick up these nested keys and move them twice.
  const seenSubs = useRef<Set<string> | null>(null);
  const subLanded = (id: string) => seenSubs.current !== null && !seenSubs.current.has(id);
  useEffect(() => { seenSubs.current = new Set(subs.map((s) => s.id)); });

  // The moment a deliverable reaches Ready to Fly: the status springs in and sparks fly.
  const statusCell = useRef<HTMLDivElement>(null);
  const [burst, setBurst] = useState(0);
  useEffect(() => {
    if (!landed) return undefined;
    setBurst((n) => n + 1);
    const el = statusCell.current;
    if (el && typeof el.animate === 'function' && !reducedMotion()) {
      try {
        el.animate([{ transform: 'scale(0.86)' }, { transform: 'scale(1)' }], { duration: MS.spring, easing: EASE.springBouncy });
      } catch { /* an engine without linear() easing: the state still lands, just without the bounce */ }
    }
    const t = window.setTimeout(() => setBurst(0), 700);
    return () => window.clearTimeout(t);
  }, [landed]);

  // The last deliverable just landed: this row's check lifts off in its turn, top to bottom,
  // like drones taking off one after another.
  const check = useRef<HTMLSpanElement>(null);
  const [lifting, setLifting] = useState(false);
  useEffect(() => {
    if (!launch || !ready || reducedMotion()) return undefined;
    const el = check.current;
    const on = window.setTimeout(() => setLifting(true), launch.delay);
    const off = window.setTimeout(() => setLifting(false), launch.delay + LIFT_MS);
    if (el && typeof el.animate === 'function') {
      try {
        el.animate(
          [{ transform: 'translateY(0) scale(1)' }, { transform: 'translateY(-4px) scale(1.35)', offset: 0.4 }, { transform: 'translateY(0) scale(1)' }],
          { duration: LIFT_MS, delay: launch.delay, easing: EASE.emphasized },
        );
      } catch { /* the state still lands, just without the lift */ }
    }
    return () => { window.clearTimeout(on); window.clearTimeout(off); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launch?.id]);

  const patch = async (t: Task, body: TaskPatch, local: Partial<Task>) => {
    onTaskUpdated({ ...t, ...local });
    try {
      const r = await holdScopes([liveScope.sparkProject(detail.production.id)], updateTask(t.id, body));
      onTaskUpdated({ ...t, ...local, ...r.task, minutes: r.task.minutes ?? t.minutes });
    } catch (e) {
      toast.error(errorMessage(e, 'Could not save that change. Try again.'));
      onChanged();
    }
  };

  const assign = (t: Task, p: Person | null) => {
    patch(t, { assignee_id: p ? p.id : null }, { assignee_id: p ? p.id : null, assignee_name: p ? p.name : null });
    setPicking(null);
  };

  const start = async () => {
    try {
      const r = await startTimer(task.id);
      onRunning(r.running);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not start the timer. Try again.'));
    }
  };
  const stop = async () => {
    try {
      const r = await stopTimer();
      onRunning(null);
      if (r.entry.needs_confirm) toast.info('That timer ran past midnight. Confirm the time on My work.');
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not stop the timer. Try again.'));
    }
  };

  const remove = async (t: Task) => {
    const ok = await confirm({
      title: `Delete ${t.name}?`,
      message: t.parent_id ? 'This subtask is removed.' : 'The deliverable and its subtasks are removed.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteTask(t.id);
      onChanged();
    } catch (e) {
      toast.error(
        errorCode(e) === 'has_time'
          ? 'Time is logged on this, so it cannot be deleted. Mark it Ready to Fly instead.'
          : errorMessage(e, 'Could not delete it. Try again.'),
      );
    }
  };

  const assignee = (t: Task) => {
    const name = t.assignee_name;
    const face = <Avatar name={name || '?'} userId={t.assignee_id} color={t.assignee_avatar_color} shape={t.assignee_avatar_shape} accessory={t.assignee_avatar_accessory} tone={name ? 'solid' : 'draft'} decorative ring="ring-transparent" className="!h-5 !w-5 !text-[9px]" />;
    if (!canEdit) {
      return (
        <span className="inline-flex min-w-0 items-center gap-1.5 text-xs text-on-surface-variant">
          {face}<span className="truncate">{name || 'Unassigned'}</span>
        </span>
      );
    }
    return (
      <Tooltip label={name ? 'Change who takes it' : 'Choose who takes it'}>
        <button
          type="button"
          aria-expanded={picking === t.id}
          aria-label={name ? `${name}, assigned to ${t.name}, change` : `Unassigned, choose someone for ${t.name}`}
          onClick={() => setPicking(picking === t.id ? null : t.id)}
          className={`-mx-1 inline-flex min-h-6 min-w-0 max-w-full items-center gap-1.5 rounded-inner px-1 text-left text-xs ${
            name ? 'text-on-surface-variant hover:text-on-surface' : 'text-on-surface-variant'
          } aria-expanded:bg-surface-container-high ${STATE} ${CONTROL_MOTION} ${FOCUS} ${TOUCH}`}
        >
          {face}<span className="truncate">{name || 'Unassigned'}</span>
        </button>
      </Tooltip>
    );
  };

  return (
    <li data-flip-key={task.id} className="rounded-control bg-surface-container">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-1.5 pl-1 pr-2">
        <div className="min-w-0 flex-[1_1_15rem]">
          <Tooltip label={open ? 'Hide subtasks and review' : 'Show subtasks and review'}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              className={`flex min-h-7 w-full min-w-0 items-center gap-1.5 rounded-control px-1 text-left ${STATE} ${CONTROL_MOTION} ${FOCUS}`}
            >
              <span className="flex h-5 w-5 flex-none items-center justify-center text-on-surface-variant"><IconChevron open={open} /></span>
              <span
                ref={check}
                aria-hidden="true"
                className={`inline-flex flex-none overflow-hidden text-green-300 transition-[width,opacity,filter] duration-standard ease-emphasized ${ready ? 'w-4 opacity-100' : 'w-0 opacity-0'} ${
                  lifting ? 'drop-shadow-[0_0_6px_rgba(134,239,172,0.9)]' : ''
                }`}
              >
                <IconCheck className="h-4 w-4 flex-none" draw={landed} />
              </span>
              <span className={`min-w-0 truncate text-sm font-semibold leading-5 ${closed ? 'text-on-surface-variant' : 'text-on-surface'}`}>{task.name}</span>
              <Appear show={task.is_stock} className="inline-flex flex-none"><Tag hex={CATEGORY.sky.ink.hex}>Stock</Tag></Appear>
            </button>
          </Tooltip>
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 pl-7">
            {assignee(task)}
            <Appear show={!!task.moved_from_name} className="truncate text-xs text-on-surface-variant">From {task.moved_from_name}</Appear>
            <Appear show={!!task.note} className="min-w-0 truncate text-xs text-on-surface-variant">{task.note}</Appear>
          </div>
        </div>

        <div className="ml-auto flex w-full min-w-0 items-center gap-2 pl-7 sm:w-auto sm:flex-none sm:pl-0">
          <div className="w-[6.5rem]">
            {canEdit ? (
              <DueDate task={task} onChange={(v) => patch(task, { due_date: v }, { due_date: v })} />
            ) : (
              <span className="block px-2.5 text-xs tabular-nums text-on-surface-variant">{task.due_date ? dayText(task.due_date, 'MMM D') : 'No date'}</span>
            )}
          </div>
          <div ref={statusCell} className="relative min-w-0 flex-1 sm:w-[8.75rem] sm:flex-none">
            {canEdit && isHand(task.status) ? (
              <Select
                size="sm"
                label={`Status of ${task.name}`}
                options={STATUS_OPTIONS}
                value={task.status}
                onChange={(k) => patch(task, { status: k as 'ready' }, { status: k as Task['status'] })}
                className={burst > 0 ? 'shadow-glow-soft' : ''}
              />
            ) : (
              <StatusPlate status={task.status} />
            )}
            {burst > 0 && <SparkBurst key={burst} />}
          </div>
          <div className="flex flex-none justify-end sm:w-[5.75rem]">
            {canEdit ? (
              <TimerKey task={task} running={running} onStart={start} onStop={stop} />
            ) : task.minutes !== undefined && task.minutes > 0 ? (
              <span className="text-xs tabular-nums text-on-surface-variant">{fmtMinutes(task.minutes)}</span>
            ) : null}
          </div>
        </div>
      </div>

      <Reveal show={picking === task.id}>
        <div className="px-2 pb-2 pl-8">
          <AssigneePicker task={task} eligibilityName={task.name} people={people} onPick={(p) => assign(task, p)} onClose={() => setPicking(null)} />
        </div>
      </Reveal>

      <Reveal show={open}>
        <div className="space-y-3 px-2 pb-3 pl-8">
          <Reveal show={subs.length > 0}>
            <ul aria-label={`Subtasks of ${task.name}`} className="m-0 list-none space-y-0.5 p-0">
              {subs.map((s) => (
                <li key={s.id} className={`rounded-control bg-surface-container-high ${subLanded(s.id) ? 'motion-safe:animate-rowLand' : ''}`}>
                  <div className="flex min-h-9 flex-wrap items-center gap-x-3 gap-y-1 px-2.5 py-1">
                    {canEdit ? (
                      <Checkbox
                        fill
                        tone="good"
                        className="flex-1"
                        checked={s.status === 'ready'}
                        onChange={(e) => patch(s, { status: e.target.checked ? 'ready' : 'not_started' }, { status: e.target.checked ? 'ready' : 'not_started' })}
                        label={(
                          <span
                            className={`block truncate decoration-on-surface-variant transition-[color] duration-standard ease-emphasized ${
                              s.status === 'ready' ? 'text-on-surface-variant line-through' : ''
                            }`}
                          >
                            {s.name}
                          </span>
                        )}
                      />
                    ) : (
                      <span className="min-w-0 flex-1 truncate text-sm text-on-surface">{s.name}</span>
                    )}
                    {assignee(s)}
                    {canEdit && (
                      <Tooltip label="Delete subtask">
                        <IconButton size="sm" label={`Delete subtask ${s.name}`} onClick={() => remove(s)}>
                          <IconClose className="h-3.5 w-3.5" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                  <Reveal show={picking === s.id}>
                    <div className="px-1.5 pb-1.5">
                      <AssigneePicker task={s} eligibilityName={task.name} people={people} onPick={(p) => assign(s, p)} onClose={() => setPicking(null)} />
                    </div>
                  </Reveal>
                </li>
              ))}
            </ul>
          </Reveal>
          {canEdit && (
            <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
              <AddLine
                className="min-w-0 flex-1"
                label="Add subtask"
                placeholder="What is the step?"
                onAdd={async (name) => {
                  try {
                    await addTask(detail.production.id, { name, parent_id: task.id });
                    onChanged();
                  } catch (e) {
                    toast.error(errorMessage(e, 'Could not add the subtask. Try again.'));
                    throw e;
                  }
                }}
              />
              <Checkbox
                size="sm"
                className="min-h-7"
                checked={task.is_stock}
                onChange={(e) => patch(task, { is_stock: e.target.checked }, { is_stock: e.target.checked })}
                label="Stock: keep this deliverable if the show is cancelled"
              />
            </div>
          )}
          <SparkReviewPanel task={task} detail={detail} people={people} studio={studio} approver={approver} archived={archived} userId={userId} onChanged={onChanged} />
          {canEdit && (
            <div>
              <Button variant="danger" size="sm" onClick={() => remove(task)}>Delete deliverable</Button>
            </div>
          )}
        </div>
      </Reveal>
    </li>
  );
};

const SparkDeliverables: React.FC<ListProps> = (props) => {
  const { detail, studio } = props;
  const toast = useToast();
  const list = useRef<HTMLUListElement>(null);
  const top = detail.tasks.filter((t) => !t.parent_id).sort((a, b) => a.position - b.position);
  const ready = top.filter((t) => t.status === 'ready').length;
  const open = top.filter((t) => t.status !== 'closed');
  const showTime = top.some((t) => t.minutes !== undefined);
  const total = detail.tasks.reduce((m, t) => m + (t.minutes || 0), 0);
  const counted = useTicker(ready);
  useFlip(list, [top.map((t) => t.id).join(',')]);

  // The last one in: every check lifts in sequence, then the bar's sheen plays. On a page that
  // loads already complete nothing launches (useBecame only fires on the change).
  const allReady = open.length > 0 && ready === open.length;
  const launched = useBecame(allReady);
  const [launchId, setLaunchId] = useState(0);
  const [sheen, setSheen] = useState(false);
  const readyOrder = open.filter((t) => t.status === 'ready').map((t) => t.id);
  useEffect(() => {
    if (!launched) { setSheen(false); return undefined; }
    setLaunchId((n) => n + 1);
    const t = window.setTimeout(() => setSheen(true), reducedMotion() ? 0 : readyOrder.length * LIFT_STEP_MS + LIFT_MS * 0.6);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launched]);

  return (
    <Panel
      title="Deliverables"
      actions={(
        <span className="text-sm tabular-nums text-on-surface-variant">
          {counted} of {open.length} ready{showTime && total > 0 ? `, ${fmtMinutes(total)} logged` : ''}
        </span>
      )}
    >
      <Reveal show={open.length > 0}>
        <Progress value={ready} max={open.length} label="Deliverables ready to fly" celebrate={allReady && (!launched || sheen)} className="mb-3" />
      </Reveal>
      {top.length === 0 ? (
        <FadeIn key="empty">
          <EmptyState variant="line">
            {studio ? 'No deliverables yet. Add the first one and give it a due date.' : 'No deliverables yet. The Studio adds them as the project takes shape.'}
          </EmptyState>
        </FadeIn>
      ) : (
        <ul ref={list} className="m-0 list-none space-y-1 p-0">
          {top.map((t) => (
            <DeliverableRow
              key={t.id}
              {...props}
              task={t}
              subs={detail.tasks.filter((s) => s.parent_id === t.id).sort((a, b) => a.position - b.position)}
              launch={launchId > 0 && readyOrder.includes(t.id) ? { id: launchId, delay: readyOrder.indexOf(t.id) * LIFT_STEP_MS } : undefined}
            />
          ))}
        </ul>
      )}
      {studio && (
        <AddLine
          className="mt-2"
          label="Add deliverable"
          placeholder="Name the deliverable"
          onAdd={async (name) => {
            try {
              await addTask(detail.production.id, { name });
              props.onChanged();
            } catch (e) {
              toast.error(errorMessage(e, 'Could not add the deliverable. Try again.'));
              throw e;
            }
          }}
        />
      )}
    </Panel>
  );
};

export default SparkDeliverables;
